import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartService } from '../services/cartService';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        setCart(null);
        setCartCount(0);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchCart = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const data = await cartService.getCart(user.uid);
      setCart(data);
      setCartCount(data?.itemCount || 0);
    } catch (error) {
      console.error('Error fetching cart context:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchCart();
    }
  }, [user, fetchCart]);

  const addToCart = async (courseId, resourceType) => {
    if (!user) {
      throw new Error('You must be logged in to add to cart.');
    }
    try {
      await cartService.addToCart(user.uid, courseId, resourceType);
      await fetchCart();
      return true;
    } catch (error) {
      throw error;
    }
  };

  const removeFromCart = async (courseId) => {
    if (!user) return;
    try {
      await cartService.removeFromCart(user.uid, courseId);
      await fetchCart();
    } catch (error) {
      throw error;
    }
  };

  const clearCart = async () => {
    if (!user) return;
    try {
      await cartService.clearCart(user.uid);
      await fetchCart();
    } catch (error) {
      throw error;
    }
  };

  const isInCart = (courseId) => {
    if (!cart || !cart.items) return false;
    return cart.items.some(item => item.courseId === courseId);
  };

  return (
    <CartContext.Provider value={{
      cart,
      cartCount,
      loading,
      fetchCart,
      addToCart,
      removeFromCart,
      clearCart,
      isInCart
    }}>
      {children}
    </CartContext.Provider>
  );
};
